/**
 * As regras do painel de anúncios (0162) que não dependem de tela: totais, custo
 * por lead, retorno e como dizer o estado da campanha e da conta.
 *
 * Número da plataforma e número do CRM ficam separados: a Meta conta formulário e
 * conversa iniciada; o CRM conta o lead que a equipe cadastrou e ligou à campanha.
 */

import type { ContaDeAnuncioDaClinica, LinhaDoPainelDeAnuncios } from './tipos';

export const PERIODOS_DO_PAINEL = [7, 30, 90] as const;

export interface TotaisDoPainel {
    investidoCentavos: number;
    impressoes: number;
    cliques: number;
    /** Formulário de lead e conversa iniciada, como a Meta conta. */
    contatosNaPlataforma: number;
    leadsCrm: number;
    ganhos: number;
    receitaCentavos: number;
    /** Investido ÷ leads do CRM. Ausente sem lead ou sem gasto. */
    custoPorLeadCentavos?: number;
    /** Receita ÷ investido. Ausente sem gasto. */
    retorno?: number;
    /** Cliques ÷ impressões, em %. */
    ctr?: number;
}

export function derivados(l: Pick<LinhaDoPainelDeAnuncios, 'investidoCentavos' | 'impressoes' | 'cliques' | 'leadsCrm' | 'receitaCentavos'>):
    Pick<TotaisDoPainel, 'custoPorLeadCentavos' | 'retorno' | 'ctr'> {
    return {
        ...(l.investidoCentavos > 0 && l.leadsCrm > 0 ? { custoPorLeadCentavos: Math.round(l.investidoCentavos / l.leadsCrm) } : {}),
        ...(l.investidoCentavos > 0 ? { retorno: Math.round((l.receitaCentavos / l.investidoCentavos) * 10) / 10 } : {}),
        ...(l.impressoes > 0 ? { ctr: Math.round((l.cliques / l.impressoes) * 1000) / 10 } : {}),
    };
}

export function totaisDoPainel(linhas: LinhaDoPainelDeAnuncios[]): TotaisDoPainel {
    const soma = linhas.reduce((t, l) => ({
        investidoCentavos: t.investidoCentavos + l.investidoCentavos,
        impressoes: t.impressoes + l.impressoes,
        cliques: t.cliques + l.cliques,
        contatosNaPlataforma: t.contatosNaPlataforma + l.leadsPlataforma + l.conversasIniciadas,
        leadsCrm: t.leadsCrm + l.leadsCrm,
        ganhos: t.ganhos + l.ganhos,
        receitaCentavos: t.receitaCentavos + l.receitaCentavos,
    }), { investidoCentavos: 0, impressoes: 0, cliques: 0, contatosNaPlataforma: 0, leadsCrm: 0, ganhos: 0, receitaCentavos: 0 });
    return { ...soma, ...derivados(soma) };
}

export type TomDoStatus = 'success' | 'warning' | 'neutral' | 'danger';

/** O `effective_status` da Meta em português. */
export function statusDaCampanha(status?: string): { rotulo: string; tom: TomDoStatus } {
    switch (status) {
        case 'ACTIVE': return { rotulo: 'Ativa', tom: 'success' };
        case 'PAUSED':
        case 'CAMPAIGN_PAUSED':
        case 'ADSET_PAUSED': return { rotulo: 'Pausada', tom: 'neutral' };
        case 'IN_PROCESS':
        case 'PENDING_REVIEW': return { rotulo: 'Em análise', tom: 'warning' };
        case 'WITH_ISSUES':
        case 'DISAPPROVED': return { rotulo: 'Com problema', tom: 'danger' };
        case 'ARCHIVED': return { rotulo: 'Arquivada', tom: 'neutral' };
        case 'DELETED': return { rotulo: 'Excluída', tom: 'neutral' };
        default: return { rotulo: status ? status.toLowerCase().replace(/_/g, ' ') : '—', tom: 'neutral' };
    }
}

const OBJETIVOS: Record<string, string> = {
    OUTCOME_LEADS: 'Leads',
    OUTCOME_ENGAGEMENT: 'Engajamento',
    OUTCOME_TRAFFIC: 'Tráfego',
    OUTCOME_AWARENESS: 'Reconhecimento',
    OUTCOME_SALES: 'Vendas',
    OUTCOME_APP_PROMOTION: 'Promoção de app',
};

export function objetivoDaCampanha(objetivo?: string): string | undefined {
    return objetivo ? OBJETIVOS[objetivo] ?? objetivo.toLowerCase().replace(/^outcome_/, '').replace(/_/g, ' ') : undefined;
}

/** O que dizer sobre a conta ligada. Nulo quando está tudo certo. */
export function avisoDaConta(conta: ContaDeAnuncioDaClinica, agora: Date = new Date()): { tom: 'warning' | 'danger'; titulo: string; texto: string } | null {
    if (conta.situacao === 'revogada') {
        return { tom: 'danger', titulo: 'A Meta recusou o acesso', texto: 'O login venceu ou foi retirado. Conecte a conta de novo para voltar a ler os números.' };
    }
    if (conta.situacao === 'erro') {
        return { tom: 'warning', titulo: 'A última leitura falhou', texto: conta.ultimoErro ?? 'A Meta não respondeu. A próxima tentativa é automática.' };
    }
    if (conta.situacao === 'escolhendo') {
        return { tom: 'warning', titulo: 'Falta escolher a conta de anúncios', texto: 'O login deu certo; escolha qual conta de anúncios é a da clínica.' };
    }
    if (conta.tokenExpiraEm) {
        const dias = Math.floor((Date.parse(conta.tokenExpiraEm) - agora.getTime()) / 86_400_000);
        if (dias <= 7) {
            return {
                tom: 'warning',
                titulo: dias < 0 ? 'O login da Meta venceu' : `O login da Meta vence em ${dias} ${dias === 1 ? 'dia' : 'dias'}`,
                texto: 'Conecte de novo para os números continuarem chegando.',
            };
        }
    }
    return null;
}

/** Valor em centavos na moeda da conta (BRL na maioria das clínicas). */
export function valorNaMoeda(centavos: number, moeda = 'BRL'): string {
    try {
        return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: moeda || 'BRL' });
    } catch {
        return `${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ${moeda}`;
    }
}

/** O que a borda devolve na volta do login (`?meta-ads=`). */
export const AVISOS_DA_VOLTA_DA_META: Record<string, { title: string; description: string; tone?: 'success' | 'danger' }> = {
    conectada: { title: 'Meta Ads conectada', description: 'Os números dos últimos 30 dias já estão em Marketing › Anúncios.', tone: 'success' },
    escolher: { title: 'Login feito', description: 'Agora escolha qual conta de anúncios é a da clínica.' },
    cancelada: { title: 'Conexão cancelada', description: 'Nada foi conectado.' },
    'sem-permissao': {
        title: 'Faltou permissão na Meta',
        description: 'Na tela da Meta, autorize a leitura das contas de anúncio e conecte de novo.',
        tone: 'danger',
    },
    erro: { title: 'Não foi possível conectar a Meta', description: 'Tente de novo em instantes. Se continuar, fale com o suporte da Fórmula MED.', tone: 'danger' },
};
